import { gql } from '@apollo/client';

import Queries from '../queries';

const {
    GET_REEL,
    GET_REEL_CLIP_TRIMMING_MEDIA_ID,
} = Queries;

const GET_MEDIA_IN_REEL = gql`
    fragment mediaItem on MediaPlayback {
        isInReel
    }
`;

export const typeDefs = `
    type ReelClip {
        mediaId: String!
        title: String!
        clipSlug: String!
        feedType: String!
        thumbnailUrl: String!
        originalDuration: Float!
        offsetStartTime: Float
        offsetEndTime: Float
        trimmedDuration: Float
    }

    type Reel {
        reelTitle: String!
        thumbnailUrl: String!
        reelClips: [ReelClip]!
        duration: Float
    }

    type Mutation {
        addReelClip(slug: String!): ReelClip
        removeReelClip(slug: String!): ReelClip
        updateReelClip(
            mediaId: String!
            offsetStartTime: Float
            offsetEndTime: Float
            originalDuration: Float
        ): ReelClip
    }

    type Query {
        reelClips: [ReelClip]
    }
`;

const Mutation = {
    setReel: (_root, { reel }, { cache }) => {

        cache.writeQuery({
            query: GET_REEL,
            data: {
                reel,
            },
        });

        reel?.reelClips.forEach(clip => {

            cache.writeFragment({
                id: `MediaPlayback:${clip.mediaId}`,
                fragment: GET_MEDIA_IN_REEL,
                data: {
                    isInReel: true,
                    __typename: 'MediaPlayback',
                },
            });

        });

        return null;

    },
    resetReel: (_root, data, { cache }) => {

        const previous = cache.readQuery({ query: GET_REEL });

        previous?.reel?.reelClips.forEach(clip => {

            cache.writeFragment({
                id: `MediaPlayback:${clip.mediaId}`,
                fragment: GET_MEDIA_IN_REEL,
                data: {
                    isInReel: false,
                    __typename: 'MediaPlayback',
                },
            });

        });

        cache.writeQuery({
            query: GET_REEL,
            data: {
                reel: {
                    reelTitle: null,
                    thumbnailUrl: null,
                    duration: null,
                    reelClips: [],
                    videoUrl: null,
                    __typename: 'Reel',
                },
            },
        });

        return null;

    },
    addReelClip: (_root, { ...reelClip }, { cache }) => {

        const previous = cache.readQuery({ query: GET_REEL });

        const index = previous.reel.reelClips.findIndex(clip => clip.clipSlug === reelClip.clipSlug);
        if (index !== -1) return null;

        const newReelClip = {
            offsetStartTime: 0,
            offsetEndTime: reelClip.originalDuration,
            trimmedDuration: null,
            ...reelClip,
            __typename: 'ReelClip',
        };
        const newReel = {
            ...previous.reel,
            duration: previous.reel.duration + newReelClip.originalDuration,
            reelClips: previous.reel.reelClips.concat([newReelClip]),
        };
        const data = {
            reel: newReel,
        };

        cache.writeQuery({
            query: GET_REEL,
            data,
        });
        cache.writeFragment({
            id: `MediaPlayback:${reelClip.mediaId}`,
            fragment: GET_MEDIA_IN_REEL,
            data: {
                isInReel: true,
                __typename: 'MediaPlayback',
            },
        });

        return newReel;

    },
    removeReelClip: (_root, { mediaId }, { cache }) => {

        const previous = cache.readQuery({ query: GET_REEL });

        const index = previous.reel.reelClips.findIndex(clip => clip.mediaId === mediaId);
        if (index === -1) return null;

        const previousDuration = previous.reel.reelClips[index].trimmedDuration || previous.reel.reelClips[index].originalDuration;
        const newReel = {
            ...previous.reel,
            duration: previous.reel.duration - previousDuration,
            reelClips: previous.reel.reelClips.filter(item => item.mediaId !== mediaId),
        };
        const data = {
            reel: newReel,
        };

        cache.writeQuery({
            query: GET_REEL,
            data,
        });
        cache.writeFragment({
            id: `MediaPlayback:${mediaId}`,
            fragment: GET_MEDIA_IN_REEL,
            data: {
                isInReel: false,
                __typename: 'MediaPlayback',
            },
        });

        return newReel;

    },
    reorderReelClip: (_root, { startIndex, endIndex }, { cache }) => {

        const previous = cache.readQuery({ query: GET_REEL });

        const newReelClips = Array.from(previous.reel.reelClips);
        const [removed] = newReelClips.splice(startIndex, 1);
        newReelClips.splice(endIndex, 0, removed);

        const newReel = {
            ...previous.reel,
            reelClips: newReelClips,
        };
        const data = {
            reel: newReel,
        };

        cache.writeQuery({
            query: GET_REEL,
            data,
        });

        return newReel;

    },
    setReelClipTrimmingMediaId: (_root, { mediaId }, { client }) => {

        client.writeQuery({
            query: GET_REEL_CLIP_TRIMMING_MEDIA_ID,
            data: {
                trimmingMediaId: mediaId,
            },
        });

        return mediaId;

    },
    updateReelClip: (_root, {
        mediaId,
        offsetStartTime,
        offsetEndTime,
        originalDuration,
    }, { cache }) => {

        const previous = cache.readQuery({ query: GET_REEL });

        const index = previous.reel.reelClips.findIndex(clip => clip.mediaId === mediaId);
        if (index === -1) return null;

        const newReel = {
            ...previous.reel,
            reelClips: [...previous.reel.reelClips],
        };

        newReel.reelClips[index] = {
            ...newReel.reelClips[index],
            offsetStartTime,
            offsetEndTime,
            originalDuration,
            trimmedDuration: offsetEndTime - offsetStartTime,
        };

        const newDuration = newReel.reelClips.reduce((acc, clip) => (acc + (clip.trimmedDuration || clip.originalDuration)), 0);

        const data = {
            reel: {
                ...newReel,
                duration: newDuration,
            },
        };

        cache.writeQuery({
            query: GET_REEL,
            data,
        });

        return newReel;

    },
};

const MediaPlayback = {
    isInReel: (parent, _args, { cache }) => {

        const { reel } = cache.readQuery({ query: GET_REEL });

        return reel.reelClips.findIndex(item => item.clipSlug === parent.slug) >= 0;

    },
};

export const resolvers = {
    MediaPlayback,
    Mutation,
};
